import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackBar } from '../../../../components/FlowBuilder/Common/SnackBar/index.tsx';
import { buildWorkspaceRoute } from '../../utils/businessFunctionRoutes';
import { RECORD_COPY } from '../constants/recordCopy';
import { recordService } from '../services/recordService';

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

function isValidXlsx(file) {
  if (!file) return false;
  const name = file.name?.toLowerCase() ?? '';
  return name.endsWith('.xlsx') || file.type === XLSX_MIME;
}

function extractErrorCells(source) {
  const payload = source?.response?.data ?? source?.data ?? source;
  const data = payload?.data ?? payload;
  const cells =
    data?.general_error?.error_cells ??
    payload?.general_error?.error_cells ??
    data?.error_cells ??
    payload?.error_cells ??
    null;
  return cells && typeof cells === 'object' && Object.keys(cells).length > 0 ? cells : null;
}

function extractErrorMessage(source, fallback) {
  const payload = source?.response?.data ?? source?.data ?? source;
  const data = payload?.data ?? payload;

  const generalError = data?.general_error ?? payload?.general_error;
  if (typeof generalError === 'string' && generalError.trim()) {
    return generalError.trim();
  }
  if (typeof generalError?.message === 'string' && generalError.message.trim()) {
    return generalError.message.trim();
  }

  const message = payload?.message ?? data?.message;
  if (typeof message === 'string' && message.trim() && !message.includes('AxiosError')) {
    return message.trim();
  }

  const detail = payload?.detail ?? data?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return detail.trim();
  }

  const error = payload?.error ?? data?.error;
  if (typeof error === 'string' && error.trim()) {
    return error.trim();
  }
  if (typeof error?.message === 'string' && error.message.trim()) {
    return error.message.trim();
  }

  return fallback;
}

function isSuccessResponse(res) {
  if (!res || res.status !== 200) return false;
  if (res.data?.status === false) return false;
  const generalError = res.data?.data?.general_error ?? res.data?.general_error;
  if (generalError?.message || typeof generalError === 'string') return false;
  if (extractErrorCells(res)) return false;
  return true;
}

export default function useImportRecordsUpload({
  leadsFormId,
  returnState = {},
} = {}) {
  const navigate = useNavigate();
  const { showSnackBar } = useSnackBar();
  const [selectedFile, setSelectedFile] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorCells, setErrorCells] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloadingSample, setIsDownloadingSample] = useState(false);
  const [isDownloadingErrors, setIsDownloadingErrors] = useState(false);

  const navigateBack = useCallback(() => {
    const { accountId, workspaceId } = returnState;
    if (accountId && workspaceId) {
      navigate(buildWorkspaceRoute(accountId, workspaceId));
      return;
    }
    navigate(-1);
  }, [navigate, returnState]);

  const handleFileSelect = useCallback((file) => {
    setErrorCells(null);
    setErrorMessage('');
    if (!file) {
      setSelectedFile(null);
      return;
    }
    if (!isValidXlsx(file)) {
      setSelectedFile(null);
      setErrorMessage(RECORD_COPY.import.errors.invalidType);
      return;
    }
    setSelectedFile(file);
  }, []);

  const handleDownloadSample = useCallback(async () => {
    if (!leadsFormId) {
      setErrorMessage(RECORD_COPY.import.errors.missingForm);
      return;
    }
    setIsDownloadingSample(true);
    try {
      await recordService.downloadImportSampleFile(leadsFormId);
    } catch {
      showSnackBar(RECORD_COPY.errors.downloadFailed ?? 'Download failed.', 'error');
    } finally {
      setIsDownloadingSample(false);
    }
  }, [leadsFormId, showSnackBar]);

  const handleDownloadErrorFile = useCallback(async () => {
    if (!selectedFile || !errorCells) {
      showSnackBar(RECORD_COPY.import.errors.noErrorData, 'warning');
      return;
    }
    setIsDownloadingErrors(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('error_cells', JSON.stringify(errorCells));
      await recordService.downloadImportErrorFile(formData);
      showSnackBar(RECORD_COPY.import.success.errorFileDownloaded, 'success');
    } catch {
      showSnackBar(RECORD_COPY.import.errors.errorDownloadFailed, 'error');
    } finally {
      setIsDownloadingErrors(false);
    }
  }, [errorCells, selectedFile, showSnackBar]);

  const handleUpload = useCallback(async () => {
    if (!selectedFile) {
      setErrorMessage(RECORD_COPY.import.errors.noFile);
      return;
    }
    if (!leadsFormId) {
      setErrorMessage(RECORD_COPY.import.errors.missingForm);
      return;
    }

    setIsUploading(true);
    setErrorMessage('');
    setErrorCells(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      const res = await recordService.uploadImportFile(leadsFormId, formData);

      const isSuccess = isSuccessResponse(res);
      const cells = extractErrorCells(res);

      if (isSuccess) {
        showSnackBar(RECORD_COPY.import.success.uploaded, 'success');
        navigateBack();
        return;
      }

      if (cells) {
        setErrorCells(cells);
      }

      const errorMsg = extractErrorMessage(res, RECORD_COPY.import.errors.uploadFailed);
      setErrorMessage(errorMsg);
      showSnackBar(errorMsg, 'error');
    } catch (err) {
      const cells = extractErrorCells(err);
      if (cells) {
        setErrorCells(cells);
      }
      const errorMsg = extractErrorMessage(err, RECORD_COPY.import.errors.uploadError);
      setErrorMessage(errorMsg);
      showSnackBar(errorMsg, 'error');
    } finally {
      setIsUploading(false);
    }
  }, [leadsFormId, navigateBack, selectedFile, showSnackBar]);

  return {
    selectedFile,
    errorMessage,
    errorCells,
    isUploading,
    isDownloadingSample,
    isDownloadingErrors,
    handleFileSelect,
    handleDownloadSample,
    handleDownloadErrorFile,
    handleUpload,
    navigateBack,
  };
}
